// Newsletter resource — a staff-authored newsletter built on a newsletter template. Matches
// docs/api-types/admin-newsletters.md (manager-backend).
//
// Two shapes:
//   • the LIST endpoint returns lightweight SUMMARIES — no `content` bag / `html` body, plus
//     `html_bytes` (the produced HTML's byte length);
//   • show / create / update / duplicate / send return the FULL object (with `content` + `html`).
//
// Lifecycle (server-controlled, never set from the body): `draft` → `queued` → `sending` → `sent`;
// a render failure parks a send at `failed`. Only a plain `draft` may be edited or (re)queued.
//
// Scheduling: `POST …/send` accepts an optional `not_before` (ISO-8601 datetime). A queued newsletter
// with a FUTURE `not_before` is held in `queued` (`is_scheduled: true`) until due, then sent on the
// worker's next tick. Cancel a scheduled send by DELETING the queued row before it fires.
//
// DELETE is a state machine: `sending` is refused; a not-sent newsletter (draft/queued/failed) is
// hard-deleted; a `sent` (not-archived) one is ARCHIVED (soft delete); an already-archived one is
// hard-deleted — so a sent newsletter must pass through the archived state before it can be removed.

export const NEWSLETTER_STATUSES = ['draft', 'queued', 'sending', 'sent', 'failed'] as const;

export type NewsletterStatus = (typeof NEWSLETTER_STATUSES)[number];

const NEWSLETTER_STATUS_LABELS: Record<NewsletterStatus, string> = {
  draft: 'Draft',
  queued: 'Queued',
  sending: 'Sending',
  sent: 'Sent',
  failed: 'Failed',
};

/** Friendly label for a status, falling back to the capitalised raw value for unknown ones. */
export function newsletterStatusLabel(status: string): string {
  return (
    NEWSLETTER_STATUS_LABELS[status as NewsletterStatus] ??
    (status ? `${status.charAt(0).toUpperCase()}${status.slice(1)}` : 'Unknown')
  );
}

/** The editable-zone bag: a flat map of zone name → value the backend renders through the template. */
export type NewsletterContent = Record<string, unknown>;

/** Fields shared by the list summary and the full newsletter. */
interface NewsletterBase {
  id: number;
  /** FK to the `newsletter_template` this newsletter is built on. */
  newsletter_template_id: number;
  /** Email subject / title (1–255 chars). */
  subject: string;
  status: NewsletterStatus;
  /** Derived: `status === 'sent'`. */
  is_sent: boolean;
  /** When it was sent; `null` until sent. */
  sent_at: string | null;
  /** Scheduled-send gate (raw DB `YYYY-MM-DD HH:MM:SS`, `APP_TIMEZONE` wall-clock); the worker holds a
   *  queued newsletter until this moment. `null` = send as soon as queued. Retained after sending. */
  not_before: string | null;
  /** Derived: a FUTURE send is still pending — `not_before` is set and not yet reached. Flips to
   *  `false` once due (and stays `false` on a sent newsletter, whose past `not_before` is kept). */
  is_scheduled: boolean;
  /** Derived: `archived_at` is set. */
  is_archived: boolean;
  /** Soft-delete timestamp; `null` while active. */
  archived_at: string | null;
  /** Creation timestamp (raw DB `YYYY-MM-DD HH:MM:SS`), or `null`. */
  created_at: string | null;
  /** Last-write timestamp (raw DB `YYYY-MM-DD HH:MM:SS`), or `null`. */
  updated_at: string | null;
}

/** Row shape from `GET /admin/newsletters` — no `content`/`html`, plus the produced HTML's size. */
export interface NewsletterSummary extends NewsletterBase {
  /** Byte length of the produced `html` body (0 before any HTML has been produced/sent). */
  html_bytes: number;
}

/** Full newsletter from show/create/update/duplicate/send — carries the editable zones and HTML. */
export interface Newsletter extends NewsletterBase {
  /** Editable-zone values captured by the UI; `null` when unset. */
  content: NewsletterContent | null;
  /** The rendered HTML body (the send worker's sent snapshot); `null` before a send. */
  html: string | null;
}

/** Body for `POST /admin/newsletters` — creates a draft. */
export interface NewsletterCreateRequest {
  newsletter_template_id: number;
  /** 1–255 characters. */
  subject: string;
  /** Editable-zone values, or null. */
  content?: NewsletterContent | null;
  /** UI-produced HTML body, or null. */
  html?: string | null;
  /** ISO-8601 datetime to pre-schedule the send, or `null`/`''` to clear (send as soon as queued). */
  not_before?: string | null;
}

/** Body for `PATCH /admin/newsletters/{id}` — partial; at least one field, draft only. */
export interface NewsletterUpdateRequest {
  newsletter_template_id?: number;
  subject?: string;
  content?: NewsletterContent | null;
  html?: string | null;
  /** ISO-8601 datetime to schedule the send, or `null`/`''` to clear (send as soon as queued). */
  not_before?: string | null;
}

/** Body for `POST /admin/newsletters/{id}/send` — all fields optional. */
export interface NewsletterSendRequest {
  /**
   * ISO-8601 datetime to schedule the send; `null`/`''` clears any schedule (send immediately). Omit
   * the key entirely to keep whatever `not_before` the draft already carries.
   */
  not_before?: string | null;
}
